import { GenericExternalLink } from "@components/generic-external-link";
import { NoDataView } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { H1 } from "@components/utilities";
import { type GetOverviewSnapshot_me_overviewPage } from "@north-beam/nb-client-side";
import { todayLocalAsISODate } from "@north-beam/nb-common";
import { FormattedDateRange } from "@shared/date-range-picker";
import { parseDateRangeArgument } from "@utils/index";

import { BreakdownView } from "./breakdown-view";
import { MetricCharts } from "./metric-charts";
import { type FetchReportParams } from "./overview";

export const CashSnapshot = ({
  response: { snapshotMetrics, snapshotBreakdownTableRows },
  bodyState,
  breakdown,
  isLoading,
}: FetchReportParams<GetOverviewSnapshot_me_overviewPage>) => {
  const { user } = useUser();
  const { dateRange: dr, attributionModel } = bodyState;
  const anchor = todayLocalAsISODate();
  const dateRange = parseDateRangeArgument(dr, anchor);

  let lowerSection = <NoDataView />;

  if (snapshotMetrics && snapshotBreakdownTableRows) {
    lowerSection = (
      <>
        <div
          className="grid grid-cols-2 grid-rows-2 gap-4 pb-4"
          data-test="snapshot-chart"
        >
          <MetricCharts
            metricsOverview={snapshotMetrics}
            isLoading={isLoading}
          />
        </div>
        <div className="nb-breakdown-table" data-test="snapshot-breakdown">
          <BreakdownView
            breakdownTitle="Breakdown (Cash snapshot)"
            rows={snapshotBreakdownTableRows}
            isLoading={isLoading}
            attributionWindow="infinity"
            attributionModel={attributionModel}
            breakdown={breakdown}
            snapshotSection
          />
        </div>
      </>
    );
  }

  const amEnum = user.attributionMethodEnum;

  return (
    <>
      <div className="py-4">
        <H1 className="flex-horizontal-center">
          Cash snapshot{" "}
          <span className="ml-2 text-sm font-light bg-[#F8F8F9] py-0.5 px-3 border rounded-md">
            <FormattedDateRange dateRange={dateRange} />
          </span>
        </H1>
        <div className="nb-accounting-methods-short-explanation">
          This model attributes conversions to the{" "}
          <strong>day that they occurred</strong>, not to prior marketing
          efforts.
        </div>
        <div className="nb-accounting-methods-read-more mt-2 flex-horizontal-center space-x-4">
          <span>
            <GenericTooltip
              className="mr-1"
              content={
                <>
                  Your current model is
                  <br />
                  <b>{amEnum.toReactSelect(attributionModel).label}</b>
                </>
              }
            />
            Attribution models{" "}
            <GenericExternalLink href="https://docs.northbeam.io/docs/attribution-models">
              read more
            </GenericExternalLink>
          </span>
          <span>
            Cash accounting{" "}
            <GenericExternalLink href="https://docs.northbeam.io/docs/accounting-modes">
              read more
            </GenericExternalLink>
          </span>
        </div>
      </div>
      {lowerSection}
    </>
  );
};
